import { useEffect, useRef, useCallback } from 'react'
import { io } from 'socket.io-client'

/**
 * Singleton WebSocket: reconnects only when token / enabled changes.
 * Callbacks are stored in refs to avoid infinite disconnect/reconnect on parent re-renders.
 */
export function useChatSocket({ token, onMessage, onMessageRead, onGroupRead, onPresence, onTyping, onGroupCreated, onGroupDissolved, onGroupMembersAdded, onGroupMemberLeft, onGroupLeft, onTrtcCall, onTrtcCallEnded, enabled = true }) {
  const socketRef = useRef(null)
  const onMessageRef = useRef(onMessage)
  const onMessageReadRef = useRef(onMessageRead)
  const onGroupReadRef = useRef(onGroupRead)
  const onPresenceRef = useRef(onPresence)
  const onTypingRef = useRef(onTyping)
  const onGroupCreatedRef = useRef(onGroupCreated)
  const onGroupDissolvedRef = useRef(onGroupDissolved)
  const onGroupMembersAddedRef = useRef(onGroupMembersAdded)
  const onGroupMemberLeftRef = useRef(onGroupMemberLeft)
  const onGroupLeftRef = useRef(onGroupLeft)
  const onTrtcCallRef = useRef(onTrtcCall)
  const onTrtcCallEndedRef = useRef(onTrtcCallEnded)

  useEffect(() => {
    onMessageRef.current = onMessage
    onMessageReadRef.current = onMessageRead
    onGroupReadRef.current = onGroupRead
    onPresenceRef.current = onPresence
    onTypingRef.current = onTyping
    onGroupCreatedRef.current = onGroupCreated
    onGroupDissolvedRef.current = onGroupDissolved
    onGroupMembersAddedRef.current = onGroupMembersAdded
    onGroupMemberLeftRef.current = onGroupMemberLeft
    onGroupLeftRef.current = onGroupLeft
    onTrtcCallRef.current = onTrtcCall
    onTrtcCallEndedRef.current = onTrtcCallEnded
  })

  useEffect(() => {
    if (!token || !enabled) {
      if (socketRef.current) {
        socketRef.current.removeAllListeners()
        socketRef.current.disconnect()
        socketRef.current = null
      }
      return undefined
    }

    const socket = io({
      path: '/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 8,
      reconnectionDelay: 1500,
      reconnectionDelayMax: 8000,
    })

    socket.on('message', (row) => onMessageRef.current?.(row))
    socket.on('message_read', (data) => onMessageReadRef.current?.(data))
    socket.on('group_read', (data) => onGroupReadRef.current?.(data))
    socket.on('presence', (data) => onPresenceRef.current?.(data))
    socket.on('typing', (data) => onTypingRef.current?.(data))
    socket.on('group_created', (data) => onGroupCreatedRef.current?.(data))
    socket.on('group_dissolved', (data) => onGroupDissolvedRef.current?.(data))
    socket.on('group_members_added', (data) => onGroupMembersAddedRef.current?.(data))
    socket.on('group_member_left', (data) => onGroupMemberLeftRef.current?.(data))
    socket.on('group_left', (data) => onGroupLeftRef.current?.(data))
    socket.on('trtc:call-incoming', (data) => onTrtcCallRef.current?.(data))
    socket.on('trtc:call-ended', (data) => onTrtcCallEndedRef.current?.(data))
    socket.on('connect_error', (err) => {
      console.warn('chat socket connect_error', err.message)
    })

    socketRef.current = socket

    return () => {
      socket.removeAllListeners()
      socket.disconnect()
      socketRef.current = null
    }
  }, [token, enabled])

  const sendMessage = useCallback(
    (payload) =>
      new Promise((resolve, reject) => {
        const socket = socketRef.current
        if (!socket?.connected) {
          reject(new Error('Socket not connected'))
          return
        }
        socket.emit('send_message', payload, (ack) => {
          if (ack?.ok) resolve(ack.message)
          else reject(Object.assign(new Error(ack?.message || 'Send failed'), { response: { data: ack } }))
        })
      }),
    []
  )

  const emitTyping = useCallback((target, typing) => {
    if (!target) return
    if (typeof target === 'object' && target.conversationId) {
      socketRef.current?.emit('typing', { conversationId: target.conversationId, typing })
      return
    }
    if (typeof target === 'object' && target.receiverId) {
      socketRef.current?.emit('typing', { receiverId: target.receiverId, typing })
      return
    }
    socketRef.current?.emit('typing', { receiverId: target, typing })
  }, [])

  const joinConversation = useCallback((conversationId) => {
    if (!conversationId) return
    socketRef.current?.emit('join_conversation', { conversationId })
  }, [])

  const isConnected = useCallback(() => !!socketRef.current?.connected, [])

  return { sendMessage, emitTyping, joinConversation, isConnected, socket: socketRef }
}
